import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  FormControlLabel,
  Switch,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HistoryToggleOffIcon from "@mui/icons-material/HistoryToggleOff";
import { GetMasterRecordHistory, GetMasterRecordVersion } from "../../Service/_index";
import { useMasterHistoryAccess } from "./useMasterHistoryAccess";
import {
  ActionChip,
  ChangeList,
  DialogHeader,
  HistoryEmpty,
  HistoryError,
  HistorySkeleton,
  Note,
  Person,
  SnapshotGrid,
  SummaryStrip,
  VersionPill,
  navySwitchSx,
  outlinedButtonSx,
  switchLabelSx,
} from "./HistoryParts";
import {
  ACTION_SENTENCE,
  STATUS_FIELDS,
  actionMeta,
  flattenSnapshot,
  formatDateTime,
  humanizeField,
  timeAgo,
} from "./historyFormat";
import Styles from "./MasterHistory.module.scss";

const PAGE_SIZE = 20;

const VersionCard = ({ entry, isCurrent, isFocused, isLast, entityType, currentFlat, cardRef }) => {
  const meta = actionMeta(entry.action);
  const [open, setOpen] = useState(false);
  const [snap, setSnap] = useState({ status: "idle", snapshot: null, message: "" });

  const changes = entry.changes || [];
  const statusOnly =
    changes.length > 0 && changes.every((change) => STATUS_FIELDS.has(change.field));
  const showChanges = changes.length > 0 && !(statusOnly && ACTION_SENTENCE[entry.action]);
  const intro = ACTION_SENTENCE[entry.action];

  // Snapshots are not part of the timeline response, so fetch one only when asked.
  const loadSnapshot = () => {
    setSnap({ status: "loading", snapshot: null, message: "" });
    GetMasterRecordVersion(entityType, entry.entityId, entry.version).then((res) => {
      if (res.error) setSnap({ status: "error", snapshot: null, message: res.message });
      else if (!res.data) {
        setSnap({ status: "error", snapshot: null, message: "This version is no longer stored." });
      } else setSnap({ status: "ready", snapshot: res.data.snapshot, message: "" });
    });
  };

  const toggle = () => {
    if (!open && snap.status === "idle") loadSnapshot();
    setOpen((value) => !value);
  };

  return (
    <Box className={Styles.entry} ref={cardRef}>
      <Box className={Styles.rail} aria-hidden="true">
        <span className={Styles.dot} style={{ backgroundColor: meta.dot }} />
        {!isLast && <span className={Styles.connector} />}
      </Box>

      <Box className={`${Styles.card} ${isFocused ? Styles.focused : ""}`}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
            px: 1.75,
            py: 1.25,
          }}
        >
          <VersionPill version={entry.version} />
          <ActionChip action={entry.action} />
          {isCurrent && (
            <Chip
              label="Current"
              size="small"
              sx={{ height: 20, fontSize: "11px", fontWeight: 600, color: "#ffffff", backgroundColor: "#1E3A5F" }}
            />
          )}
          <Box sx={{ flex: 1 }} />
          <Person name={entry.changedByName} />
          <Tooltip title={timeAgo(entry.changedAt)}>
            <Typography sx={{ fontSize: "11.5px", color: "#667085", whiteSpace: "nowrap" }}>
              {formatDateTime(entry.changedAt)}
            </Typography>
          </Tooltip>
        </Box>

        <Box sx={{ px: 1.75, pb: 1.25, display: "flex", flexDirection: "column", gap: 1.25 }}>
          {intro && (
            <Typography sx={{ fontSize: "12.5px", color: "#475467" }}>{intro}</Typography>
          )}
          {showChanges && (
            <ChangeList changes={changes} currentFlat={isCurrent ? null : currentFlat} />
          )}
          {!intro && !changes.length && (
            <Typography sx={{ fontSize: "12.5px", color: "#667085" }}>
              No field changes were recorded.
            </Typography>
          )}

          <Box>
            <Button
              size="small"
              onClick={toggle}
              aria-expanded={open}
              endIcon={
                <ExpandMoreIcon
                  sx={{
                    transition: "transform 0.2s",
                    transform: open ? "rotate(180deg)" : "none",
                  }}
                />
              }
              sx={{ textTransform: "none", fontSize: "12px", fontWeight: 500, color: "#1E3A5F", px: 0.5 }}
            >
              {open ? "Hide full record" : `View full record at v${entry.version}`}
            </Button>
          </Box>

          {open && snap.status === "loading" && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
              <CircularProgress size={16} />
              <Typography sx={{ fontSize: "12.5px", color: "#667085" }}>Loading record…</Typography>
            </Box>
          )}
          {open && snap.status === "error" && (
            <HistoryError message={snap.message} onRetry={loadSnapshot} />
          )}
          {open && snap.status === "ready" && (
            <SnapshotGrid snapshot={snap.snapshot} currentFlat={isCurrent ? null : currentFlat} />
          )}
        </Box>
      </Box>
    </Box>
  );
};

/**
 * Version timeline for one master record. `focusVersion` scrolls to a version
 * and highlights it (used when opening from the Change Log page).
 */
const MasterHistoryDialog = ({ open, onClose, entityType, entityId, recordLabel, focusVersion }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { entities } = useMasterHistoryAccess();

  const [attempt, setAttempt] = useState(0);
  const [load, setLoad] = useState({ status: "loading", entries: [], currentVersion: 0, recordLabel: "", message: "" });
  const [compare, setCompare] = useState(false);
  const [current, setCurrent] = useState({ status: "idle", snapshot: null });
  const [showAll, setShowAll] = useState(false);
  const cardRefs = useRef({});
  const scrolled = useRef(false);
  const loadGeneration = useRef(0);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoad({ status: "loading", entries: [], currentVersion: 0, recordLabel: "", message: "" });
    setCompare(false);
    setCurrent({ status: "idle", snapshot: null });
    setShowAll(false);
    scrolled.current = false;
    loadGeneration.current += 1;

    GetMasterRecordHistory(entityType, entityId).then((res) => {
      if (cancelled) return;
      if (res.error) setLoad((prev) => ({ ...prev, status: "error", message: res.message }));
      else if (res.enabled === false) setLoad((prev) => ({ ...prev, status: "disabled" }));
      else {
        setLoad({
          status: "ready",
          entries: res.data || [],
          currentVersion: res.currentVersion || 0,
          recordLabel: res.recordLabel || "",
          message: "",
        });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [open, entityType, entityId, attempt]);

  // The newest version is what "current" means; fetch it once, on first use.
  useEffect(() => {
    if (!compare || load.status !== "ready" || current.status !== "idle") return;
    const generation = loadGeneration.current;
    setCurrent({ status: "loading", snapshot: null });
    GetMasterRecordVersion(entityType, entityId, load.currentVersion).then((res) => {
      if (generation !== loadGeneration.current) return;
      if (res.error || !res.data) setCurrent({ status: "error", snapshot: null });
      else setCurrent({ status: "ready", snapshot: res.data.snapshot });
    });
  }, [compare, load.status, load.currentVersion, current.status, entityType, entityId]);

  const currentFlat = useMemo(
    () => (compare && current.status === "ready" ? flattenSnapshot(current.snapshot) : null),
    [compare, current]
  );

  const { entries } = load;

  useEffect(() => {
    if (load.status !== "ready" || !focusVersion || scrolled.current) return;
    const index = entries.findIndex((entry) => entry.version === Number(focusVersion));
    if (index < 0) return;
    if (index >= PAGE_SIZE && !showAll) {
      setShowAll(true);
      return;
    }
    scrolled.current = true;
    cardRefs.current[focusVersion]?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [load.status, entries, focusVersion, showAll]);

  const masterLabel =
    entities.find((item) => item.entityType === entityType)?.label ||
    entries[0]?.entityLabel ||
    humanizeField(entityType);
  const shownRecord = load.recordLabel || recordLabel || "Record";

  const changeCount = entries.filter((entry) => entry.action !== "baseline").length;
  const newest = entries[0];
  const oldest = entries[entries.length - 1];
  const trimmed = Boolean(oldest && oldest.version > 1);
  const visible = showAll ? entries : entries.slice(0, PAGE_SIZE);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
      aria-labelledby="master-history-title"
    >
      <DialogHeader
        titleId="master-history-title"
        title="Change history"
        subtitle={`${masterLabel} · ${shownRecord}`}
        onClose={onClose}
        actions={
          <>
            {current.status === "loading" && <CircularProgress size={14} />}
            <FormControlLabel
              sx={switchLabelSx}
              control={
                <Switch
                  size="small"
                  sx={navySwitchSx}
                  checked={compare}
                  onChange={(event) => setCompare(event.target.checked)}
                  disabled={load.status !== "ready" || !entries.length}
                />
              }
              label="Compare with current"
            />
          </>
        }
      />

      <Box
        sx={{
          p: 2.5,
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          maxHeight: fullScreen ? "none" : "70vh",
        }}
      >
        {load.status === "loading" && <HistorySkeleton />}

        {load.status === "error" && (
          <HistoryError message={load.message} onRetry={() => setAttempt((n) => n + 1)} />
        )}

        {load.status === "disabled" && (
          <HistoryEmpty
            icon={<HistoryToggleOffIcon />}
            title="History tracking is turned off"
            text="Changes to master data are not being recorded right now."
          />
        )}

        {load.status === "ready" && !entries.length && (
          <HistoryEmpty
            title="No changes recorded yet"
            text="Once this record is edited, every version will show up here with who changed what."
          />
        )}

        {load.status === "ready" && entries.length > 0 && (
          <>
            <SummaryStrip
              items={[
                { label: "Current version", value: `v${load.currentVersion}` },
                { label: "Total changes", value: changeCount },
                { label: "Last changed by", value: newest.changedByName || "Unknown" },
                { label: "Last changed", value: formatDateTime(newest.changedAt) },
                { label: "Tracking since", value: formatDateTime(oldest.changedAt) },
              ]}
            />

            {trimmed && (
              <Note tone="warn">
                Older versions were removed to keep history at a manageable size. The earliest version
                kept is v{oldest.version}.
              </Note>
            )}

            {compare && current.status === "error" && (
              <Box sx={{ mb: 2 }}>
                <HistoryError
                  message="Could not load the current record to compare with."
                  onRetry={() => setCurrent({ status: "idle", snapshot: null })}
                />
              </Box>
            )}

            {visible.map((entry, index) => (
              <VersionCard
                key={entry.version}
                entry={entry}
                entityType={entityType}
                isCurrent={entry.version === load.currentVersion}
                isFocused={Number(focusVersion) === entry.version}
                isLast={index === visible.length - 1}
                currentFlat={currentFlat}
                cardRef={(node) => {
                  cardRefs.current[entry.version] = node;
                }}
              />
            ))}

            {!showAll && entries.length > PAGE_SIZE && (
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setShowAll(true)}
                  sx={outlinedButtonSx}
                >
                  Show older ({entries.length - PAGE_SIZE})
                </Button>
              </Box>
            )}
          </>
        )}
      </Box>
    </Dialog>
  );
};

export default MasterHistoryDialog;
